/**
 * Embed Popup Component
 * Handles popup/modal functionality for embed forms (iframes, scripts, etc.)
 * Used across multiple blocks (FAQ, Image with text and button, etc.)
 *
 * Takes the target document explicitly so the exact same implementation can
 * be instantiated against the frontend page document and, separately,
 * against the block-editor canvas iframe's own document (see
 * src/js/_editor.js) — a plain top-window `document` reference would never
 * see clicks happening inside that iframe's own DOM tree.
 */

class EmbedPopup {
  constructor(doc = document) {
    this.doc = doc;
    this.activePopup = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (this.doc.readyState === 'loading') {
      this.doc.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    // Handle popup triggers
    this.bindTriggers();

    // Handle popup closers
    this.bindClosers();

    // Handle ESC key
    this.bindEscapeKey();
  }

  bindTriggers() {
    // Delegated (like bindClosers below) rather than a listener per trigger
    // found at bind time: the editor canvas can render new triggers later
    // (ACF updates a block's preview in place on field changes), and a
    // one-time querySelectorAll snapshot would never see them.
    this.doc.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-popup-trigger]');

      if (!trigger) {
        return;
      }

      e.preventDefault();
      const popupId = trigger.getAttribute('data-popup-trigger');
      this.openPopup(popupId);
    });
  }

  bindClosers() {
    // Use event delegation for close buttons and overlays
    this.doc.addEventListener('click', (e) => {
      // Check if clicked element is a close button or overlay (support both new and legacy classes)
      const isCloseButton = e.target.classList.contains('embed-popup__close') ||
                           e.target.classList.contains('faq-popup__close');
      const isOverlay = e.target.classList.contains('embed-popup__overlay') ||
                       e.target.classList.contains('faq-popup__overlay');

      if (isCloseButton || isOverlay) {
        const popup = e.target.closest('.embed-popup') ||
                     e.target.closest('.faq-popup');
        if (popup) {
          this.closePopup(popup);
        }
      }
    });
  }

  bindEscapeKey() {
    this.doc.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activePopup) {
        this.closePopup(this.activePopup);
      }
    });
  }

  openPopup(popupId) {
    const popup = this.doc.getElementById(popupId);

    if (!popup) {
      console.warn(`Popup with id "${popupId}" not found`);
      return;
    }

    // Close any currently active popup
    if (this.activePopup && this.activePopup !== popup) {
      this.closePopup(this.activePopup);
    }

    // Remove inline hide used to prevent FOUC while CSS is deferred
    popup.style.removeProperty('display');

    // Add visible class - check if it's legacy FAQ popup or new embed popup
    const visibleClass = popup.classList.contains('faq-popup') ? 'faq-popup--visible' : 'embed-popup--visible';
    popup.classList.add(visibleClass);
    popup.setAttribute('aria-hidden', 'false');

    // Add body class - support both new and legacy
    this.doc.body.classList.add('embed-popup-open', 'popup-open');

    this.activePopup = popup;

    // Focus management for accessibility
    this.setFocusTrap(popup);
  }

  closePopup(popup) {
    if (!popup) return;

    // Remove both new and legacy visible classes
    popup.classList.remove('embed-popup--visible', 'faq-popup--visible');
    popup.setAttribute('aria-hidden', 'true');
    this.doc.body.classList.remove('embed-popup-open', 'popup-open');

    if (this.activePopup === popup) {
      this.activePopup = null;
    }
  }

  setFocusTrap(popup) {
    // Get all focusable elements within the popup
    const focusableElements = popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      // Focus the first focusable element (usually the close button)
      focusableElements[0].focus();
    }
  }
}

// Initialize popup system against the page's own document when this script
// loads normally (frontend, or a plain non-iframed admin load) — this is
// the exact, unchanged frontend contract. The editor also imports the
// `EmbedPopup` class directly (see src/js/_editor.js) to instantiate a
// second instance scoped to the canvas iframe's own document.
const embedPopupInstance = new EmbedPopup(document);

// Export for potential external use
if (typeof window !== 'undefined') {
  window.EmbedPopup = embedPopupInstance;
}

export default EmbedPopup;
